// constants/settingsConfig.js
// Configuration-driven settings definitions
// Each setting category can be rendered as its own tab

export const FISCAL_YEAR_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (€)' },
  { value: 'GBP', label: 'GBP (£)' },
  { value: 'CAD', label: 'CAD ($)' }
];

export const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' }
];

export const DATE_FORMAT_OPTIONS = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2024)' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2024)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2024-12-31)' }
];

export const EMAIL_FREQUENCY_OPTIONS = [
  { value: 'immediate', label: 'Immediate' },
  { value: 'daily', label: 'Daily Digest' },
  { value: 'weekly', label: 'Weekly Summary' },
  { value: 'never', label: 'Never' }
];

// Setting field types
export const FIELD_TYPES = {
  NUMBER: 'number',
  TEXT: 'text',
  SELECT: 'select',
  TOGGLE: 'toggle',
  EMAIL: 'email'
};

// Settings organized by category
export const SETTINGS_CONFIG = {
  defaults: {
    title: 'Default Values',
    description: 'Configure default values and targets across the system',
    fields: [
      {
        key: 'defaultRevenueGoal',
        label: 'Default Annual Revenue Goal ($)',
        type: FIELD_TYPES.NUMBER,
        helpText: 'Default annual revenue goal for new fiscal years',
        defaultValue: 1500000
      },
      {
        key: 'defaultCashRunwayTarget',
        label: 'Default Cash Runway Target (months)',
        type: FIELD_TYPES.NUMBER,
        helpText: 'Target number of months of operating expenses to maintain',
        defaultValue: 6,
        min: 1,
        max: 24
      },
      {
        key: 'profitMarginTarget',
        label: 'Target Profit Margin (%)',
        type: FIELD_TYPES.NUMBER,
        helpText: 'Your target profit margin percentage',
        defaultValue: 20,
        min: 0,
        max: 100
      }
    ]
  },
  
  system: {
    title: 'System Settings',
    description: 'Configure system-wide preferences',
    fields: [
      {
        key: 'fiscalYearStart',
        label: 'Fiscal Year Start Month',
        type: FIELD_TYPES.SELECT,
        options: FISCAL_YEAR_MONTHS.map(m => ({ value: m, label: m })),
        helpText: 'Month when your fiscal year begins',
        defaultValue: 'January'
      },
      {
        key: 'currencyFormat',
        label: 'Currency Format',
        type: FIELD_TYPES.SELECT,
        options: CURRENCY_OPTIONS,
        helpText: 'Default currency for financial displays',
        defaultValue: 'USD'
      },
      {
        key: 'timezone',
        label: 'Timezone',
        type: FIELD_TYPES.SELECT,
        options: TIMEZONE_OPTIONS,
        helpText: 'Your local timezone for date/time displays',
        defaultValue: 'America/Los_Angeles'
      },
      {
        key: 'dateFormat',
        label: 'Date Format',
        type: FIELD_TYPES.SELECT,
        options: DATE_FORMAT_OPTIONS,
        helpText: 'How dates are displayed throughout the application',
        defaultValue: 'MM/DD/YYYY'
      }
    ]
  },

  notifications: {
    title: 'Notification Preferences',
    description: 'Control how and when you receive notifications',
    fields: [
      {
        key: 'enableEmailNotifications',
        label: 'Enable Email Notifications',
        type: FIELD_TYPES.TOGGLE,
        helpText: 'Receive notifications via email',
        defaultValue: true
      },
      {
        key: 'emailFrequency',
        label: 'Email Notification Frequency',
        type: FIELD_TYPES.SELECT,
        options: EMAIL_FREQUENCY_OPTIONS,
        helpText: 'How often to receive email notifications',
        defaultValue: 'daily',
        dependsOn: 'enableEmailNotifications' // Only show if emails enabled
      },
      {
        key: 'notifyOnLowCashRunway',
        label: 'Low Cash Runway Alerts',
        type: FIELD_TYPES.TOGGLE,
        helpText: 'Get notified when cash runway falls below target',
        defaultValue: true
      },
      {
        key: 'cashRunwayThreshold',
        label: 'Cash Runway Alert Threshold (months)',
        type: FIELD_TYPES.NUMBER,
        helpText: 'Alert when cash runway drops below this many months',
        defaultValue: 3,
        min: 1,
        max: 12,
        dependsOn: 'notifyOnLowCashRunway'
      },
      {
        key: 'notifyOnOverdueAR',
        label: 'Overdue A/R Alerts',
        type: FIELD_TYPES.TOGGLE,
        helpText: 'Get notified about overdue accounts receivable',
        defaultValue: true
      },
      {
        key: 'arThresholdDays',
        label: 'A/R Alert Threshold (days)',
        type: FIELD_TYPES.NUMBER,
        helpText: 'Alert when invoices are overdue by this many days',
        defaultValue: 30,
        min: 1,
        max: 180,
        dependsOn: 'notifyOnOverdueAR'
      }
    ]
  },

  display: {
    title: 'Display Settings',
    description: 'Customize how information is displayed',
    fields: [
      {
        key: 'compactMode',
        label: 'Compact Mode',
        type: FIELD_TYPES.TOGGLE,
        helpText: 'Show more information in less space',
        defaultValue: false
      },
      {
        key: 'showCents',
        label: 'Show Cents in Currency',
        type: FIELD_TYPES.TOGGLE,
        helpText: 'Display cents/decimal places for currency values',
        defaultValue: true
      },
      {
        key: 'roundToThousands',
        label: 'Round Large Numbers',
        type: FIELD_TYPES.TOGGLE,
        helpText: 'Display large numbers as 1.5M instead of 1,500,000',
        defaultValue: false
      },
      {
        key: 'defaultAccountingMethod',
        label: 'Default Accounting Method',
        type: FIELD_TYPES.SELECT,
        options: [
          { value: 'accrual', label: 'Accrual' },
          { value: 'cash', label: 'Cash' }
        ],
        helpText: 'Default method for financial calculations',
        defaultValue: 'accrual'
      }
    ]
  }
};

// Helper function to get all default values
export const getAllDefaultValues = () => {
  const defaults = {};
  
  Object.values(SETTINGS_CONFIG).forEach(category => {
    category.fields.forEach(field => {
      defaults[field.key] = field.defaultValue;
    });
  });
  
  return defaults;
};

// Helper function to get settings for a specific category
export const getSettingsByCategory = (categoryKey) => {
  return SETTINGS_CONFIG[categoryKey] || null;
};